import { z } from 'zod'

import { moneySchema } from '@/schemas/common'

/**
 * The watchlist card on Home and its empty-state twin, "Popular stocks".
 *
 * Both render the same row, so there is one row schema and two wrappers. A
 * component written against `WatchRow` works in either card.
 */

export const watchRowSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  ltp: moneySchema.nullable(),
  percentChange: moneySchema.nullable(),
  /**
   * Up to five closes, oldest first; mm-be sends fewer for a stock listed
   * inside the window and none for one with no price yet, so no `.length()`
   * here — `Sparkline` draws nothing under two. Plain numbers because they only
   * feed the sparkline — the figure a member reads is `ltp`, which is Money.
   */
  sparkline: z.array(z.number()),
  /** Company mark URL, or null while the logo set is still being assembled. */
  logo: z.string().nullable(),
})

/**
 * One row of the "Add to watchlist" picker.
 *
 * Deliberately NOT a `WatchRow`. The picker's rows now come from
 * `/api/v5/market/search/`, whose `SearchResult` carries key, name, sector and
 * type and no price, no change and no sparkline — so a search hit widened into
 * a `WatchRow` would be three empty fields inviting a component to draw them.
 * The picker draws a symbol and a company name; this is exactly that.
 */
export const stockCandidateSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
})

export const watchlistRefSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
})

export const popularStocksSchema = z.object({
  caption: z.string().min(1),
  rows: z.array(watchRowSchema),
})

export const watchlistCollectionSchema = z.object({
  lists: z.array(
    watchlistRefSchema.extend({
      rows: z.array(watchRowSchema),
    }),
  ),
  candidates: z.array(watchRowSchema),
})

export type WatchRow = z.infer<typeof watchRowSchema>
export type StockCandidate = z.infer<typeof stockCandidateSchema>
export type WatchlistRef = z.infer<typeof watchlistRefSchema>
export type PopularStocks = z.infer<typeof popularStocksSchema>
export type WatchlistCollection = z.infer<typeof watchlistCollectionSchema>
